import * as a from '@mitranim/js/all.mjs'
import * as ob from '@mitranim/js/obs.mjs'
import * as c from './conf.mjs'
import * as u from './util.mjs'
import * as dmi from './dat-misc.mjs'
import * as dc from './dat-cat.mjs'
import * as xln from './localization/xln.mjs'

const specMixTimed = {
  createdAt: val => a.toInstOpt(val, a.DateValid),
  updatedAt: val => a.toInstOpt(val, a.DateValid),
  deletedAt: val => a.toInstOpt(val, a.DateValid),
}

const specMixImaged = {
  image: val => a.toInstOpt(val, File),
}

const specMixPersoned = {
  person: val => a.toInstOpt(val, Person),
}

const specMixCategorized = {
  mainShortCategories: val => a.toInstOpt(val, ShortCats),
  concernShortCategories: val => a.toInstOpt(val, ShortCats),
}

function MixOwned(cls) {
  return class MixOwned extends cls {
    isOwnedBy(person) {return u.teq(this.personId, u.pkOpt(person))}
    isEditableBy(person) {return this.isOwnedBy(person) || person?.isModerator}
  }
}

/*
Adapter for `BukArticle` and `BukService` that makes them similar, respectively,
to `AggArticle` and `AggService` for our UI code.
*/
function MixBukVersioned(cls) {
  return class MixBukVersioned extends u.MixBuketed(cls) {
    /*
    Questionable hack. This allows our entities to access fields which are
    provided in page items. Assigning this to entities in `SetOut` entity sets
    is incorrect, because each entity may be referenced in multiple pages
    (thus having multiple page items), where some fields may be inconsistent
    between different page items for the same entity. For example, when we
    fetch multiple service pages in one buket request, the same service may
    appear in two different pages with different rankings due to variance in
    inputs. This is an adapter for view code written for non-buket APIs. We
    should migrate view code to the buket API, use page items properly, and
    remove this.
    */
    static spec = {
      ...super.spec,
      pageItem: a.optStruct,
    }

    ext() {throw u.errImplementInSubclass()}
    agg() {throw u.errImplementInSubclass()}

    // Ext fields.
    get person() {return this.buk()?.person(this.personId)}
    get image() {return this.buk()?.file(this.imageId)}
    get preview() {return this.ext()?.preview}

    // Agg fields.
    get mainShortCategories() {return this.agg()?.mainShortCategories}
    get concernShortCategories() {return this.agg()?.concernShortCategories}
    get mainCategoryIds() {return this.agg()?.mainCategoryIds}
    get concernCategoryIds() {return this.agg()?.concernCategoryIds}
    get reaction() {return this.agg()?.reaction}
    get viewMetas() {return this.viewMeta()}
  }
}

export class Person extends u.Model {
  static spec = {
    ...super.spec,
    ...specMixTimed,
    birthday: val => a.toInstOpt(val, a.DateShort),
    practiceStartAt: val => a.toInstOpt(val, a.DateShort),
    paypalAccountEmail: a.optStr,
  }

  sitePath() {return dmi.guessPersonLink(this)}
  getName() {return dmi.guessPersonName(this)}
  getNameOpt() {return dmi.guessPersonNameOpt(this)}
  titlePreview() {return this.getNameOpt() || this.preview || this.id}
  selById() {return {title: this.getName(), value: this.id}}
  selBySlug() {return a.vac(this.slug) && {title: this.getName(), value: this.slug}}
  minimal() {return u.trimDict({id: this.id, email: this.email})}

  // Note: this uses specifically email, rather than account ID, because our
  // moderators manually trigger withdrawals for sellers by this email, not by ID.
  hasPayPal() {return !!this.paypalAccountEmail}

  // For old deprecated promotions. No longer active. TODO drop.
  isPaidPromoActive(slug) {return !!this.activePromotions?.includes(slug)}

  actInp() {return {entId: this.id}}

  // Non-standard fields for GA event.
  gtagOpt() {return {entType: c.ENT_TYPE_PERSON}}

  // Standard fields for GA item.
  gtagItem() {
    return {
      item_id: this.id,
      // Priority: from more stable to less stable.
      item_name: this.titlePreview(),
    }
  }
}

export class AggPerson extends Person {
  static spec = {
    ...super.spec,
    ...specMixCategorized,
    avatarImage: val => a.toInstOpt(val, File),
    coverImage: val => a.toInstOpt(val, File),
    certImages: val => a.toInstOpt(val, Files),
    activePromotions: u.optStrArr,
    insurances: a.optArr,
  }

  hasPaidListing() {return this.privateMeta?.hasPaidListing}
  hasSellerPrime() {return this.privateMeta?.hasSellerPrime}
  hasUnpaidListing() {return this.privateMeta?.hasUnpaidListing}
  hasPushToken() {return this.privateMeta?.hasPushToken}
  canReceiveMobileNotifications() {return this.hasPushToken()}
  hasEmentor() {return this.hasSellerPrime()}
  isEditableBy(person) {return !!person && person.id === this.id}

  // Note: ideally this would be a getter method, but a getter property is more
  // compatible with our form field utils. Also note that the backend doesn't
  // send this field.
  get insuranceNames() {return a.map(this.insurances, u.getName)}
}

export class BukPerson extends u.MixBuketed(Person) {
  ext() {return this.buk()?.personExt(this.id)}
  agg() {return this.buk()?.personAgg(this.id)}
  get avatarImage() {return this.buk()?.file(this.avatarImageId)}
  get countryName() {return this.buk().country(this.countryCode)?.name}
  get privateMeta() {return this.buk().get.units?.personPrivateMeta}
  get activePromotions() {return this.agg()?.activePromotions}
}

class PersonById extends u.ClsCollByPk {get cls() {return Person}}
class PersonBySlug extends u.ClsCollBySlug {get cls() {return Person}}

export class AggPersons extends u.ClsMultiColl_ByIdOrPk_BySlug {
  get cls() {return AggPerson}
  makeIndexes() {return [new PersonById(), new PersonBySlug()]}
  selsById() {return a.map(this, selById)}
  selsBySlug() {return a.mapCompact(this, selBySlug)}
}

export class AggPersonPage extends u.ClsPage {get cls() {return AggPersons}}
export class BukPersons extends AggPersons {get cls() {return BukPerson}}
export class BukPersonPage extends AggPersonPage {get cls() {return BukPersons}}

export class Article extends MixOwned(u.Model) {
  static spec = {
    ...super.spec,
    ...specMixTimed,
    slug: a.reqValidStr,
  }

  sitePath() {return this.constructor.sitePath(this.slug)}
  static sitePath(slug) {return dmi.articleLink(slug)}
  titlePreview() {return this.title || this.preview || u.mdPreviewShort(this.content)}
  isPending() {return this.moderationStatus === c.MODERATION_STATUS_PENDING}
  isDraft() {return !this.moderationStatus || this.moderationStatus === c.MODERATION_STATUS_RETRACTED}

  actInp() {return {entId: this.id}}

  // Non-standard fields for GA event.
  gtagOpt() {return {entType: c.ENT_TYPE_ARTICLE}}

  // Standard fields for GA item.
  gtagItem() {
    return {
      item_id: this.originId,
      item_name: this.slug || this.title,
    }
  }
}

export class AggArticle extends Article {
  static spec = {
    ...super.spec,
    ...specMixCategorized,
    ...specMixPersoned,
    ...specMixImaged,
    articleLinks: val => a.toInstOpt(val, ArticleLinks),
  }

  titlePreview() {return this.title || this.preview}
}

export class ObsAggArticle extends u.MixObs(AggArticle) {}

export class BukArticle extends MixBukVersioned(Article) {
  ext() {return this.buk()?.articleExt(this.id)}
  agg() {return this.buk()?.articleAgg(this.id)}

  viewMeta() {
    return (
      this.buk()?.viewMeta(this.originId) ??
      this.buk()?.viewMeta(this.id)
    )
  }

  get preview() {return this.ext()?.preview}

  titlePreview() {
    return this.title || this.preview || u.mdPreviewShort(this.content)
  }

  // TODO better method naming convention.
  static mutInpSets(inp) {
    a.reqInst(inp, BukInp)
    inp.get.sets.articles.enable()
    inp.get.sets.articleExts.enable()
    inp.get.sets.articleAggs.enable()
    inp.get.sets.persons.enable()
    inp.get.sets.files.enable()
    inp.get.sets.viewMetas.enable()
  }
}

export class BukArticles extends u.BukModelColl {get cls() {return BukArticle}}

export class BukArticlePage extends u.BukPage {
  coll() {return this.buk().get.sets?.articles}
}

export class BukArticlePages extends u.BukPages {get cls() {return BukArticlePage}}

export class BukArticleExt extends u.BukModel {
  static spec = {
    ...super.spec,
    ...specMixImaged,
    ...specMixPersoned,
  }
}

export class BukArticleExts extends u.BukModelColl {get cls() {return BukArticleExt}}

export class BukArticleAgg extends u.BukModel {
  static spec = {
    ...super.spec,
    ...specMixCategorized,
  }
}

export class BukArticleAggs extends u.BukModelColl {get cls() {return BukArticleAgg}}

export class Service extends MixOwned(u.Model) {
  static spec = {
    ...super.spec,
    ...specMixTimed,
    slug: a.reqValidStr,
  }

  sitePath(ctx) {
    const link = this.constructor.sitePath(this.slug)
    if (ctx && u.isUrlPreferLatest(ctx.url)) {
      link.query.set(c.QUERY_KEY_PREFER_LATEST, true)
    }
    return link
  }

  static sitePath(key) {return dmi.serviceLink(key)}

  hasAccess() {return this.isFree() || this.hasPurchasedAccess()}

  // Note: `.accessOrders` is customized by the server for the current session.
  hasPurchasedAccess() {return a.some(this.accessOrders, dmi.isAccessOrderPaid)}

  /*
  Assumes that we don't need to check the "charges" generated for this service.
  Imagine being told a service is free, then getting charged for it. We could,
  but we should't.
  */
  isFree() {return this.priceCents === 0}
  vidExternalId() {return a.procure(this.shortVids, u.getExternalId)}
  hasUploadedVid() {return a.truthy(this.vidExternalId())}
  titlePreview() {return this.title || this.preview}
  getPreview() {return this.preview || u.mdPreviewShort(this.description)}

  paymentReason() {
    const {nature, title} = this
    if (nature && title) return nature + `: ` + title
    return nature || title
  }

  /*
  TODO: Move to BukService when finish the migration.
  Image was moved to ServiceAgg.
  */
  imgUrl(size) {return u.imageUrl(this.image, size)}
  imgUrlAny(size) {return this.imgUrl(size) || c.IMAGE_PLACEHOLDER}

  bookFileDownloadPath(id) {
    return u.maybeUrl(`/api/v2/services`, a.pk(this), `book_files`, id)
  }

  actInp() {return {entId: this.id, personServicePpcId: this.personServicePpcId}}

  // Non-standard fields for GA event.
  gtagOpt() {return {entType: c.ENT_TYPE_SERVICE, serviceNature: this.nature}}

  // Standard fields for GA item.
  gtagItem() {
    return {
      item_id: this.originId,
      item_name: this.slug,
      currency: u.currencyToGtag(this.priceCurrency),
      price: u.centsToGtag(this.priceCents),
    }
  }
}

export class AggService extends Service {
  static spec = {
    ...super.spec,
    ...specMixCategorized,
    ...specMixPersoned,
    ...specMixImaged,
  }

  getPreview() {return this.preview}
}

export class BukService extends MixBukVersioned(Service) {
  ext() {return this.buk()?.serviceExt(this.id)}
  agg() {return this.buk()?.serviceAgg(this.id)}

  viewMeta() {
    return (
      this.buk()?.viewMeta(this.originId) ??
      this.buk()?.viewMeta(this.id)
    )
  }

  // Ext fields.
  get isPromoted() {return this.pageItem?.isPromoted}
  get rank() {return this.pageItem?.rank}
  get isFavorite() {return this.ext()?.isFavorite}
  get charge() {return this.ext()?.charge}
  get suitableDiscounts() {return this.ext()?.suitableDiscounts}
  get discount() {return this.ext()?.discount}
  get nextAvailableTimespan() {return this.ext()?.nextAvailableTimespan}
  get pastStartAt() {return this.ext()?.pastStartAt}
  get upcomingStartAt() {return this.ext()?.upcomingStartAt}
  get preview() {return this.ext()?.preview}

  // Agg fields.
  get reviewCount() {return this.agg()?.reviewCount}
  get rating() {return this.agg()?.rating}
  get vidIds() {return this.agg()?.vidIds}
  get shortVids() {return this.agg()?.shortVids}
  get bookFileIds() {return this.agg()?.bookFileIds}
  get bookFiles() {return this.agg()?.bookFiles}
  get childServiceOriginIds() {return this.agg()?.childServiceOriginIds}
  get imageId() {return this.agg()?.imageId}
  get image() {return this.agg()?.image}
  get fileIds() {return this.agg()?.fileIds}
  get files() {return this.agg()?.files}

  /*
  Caution: the buket API doesn't support fetching access orders for all
  services in the buket. Access orders are fetched for specific services
  on an opt-in basis. This approach to finding relevant access orders
  requires us to also fetch the specific service versions for all access
  orders. In the buket input, the set of services must be enabled.
  */
  get accessOrders() {
    const id = a.reqPk(this.originId)
    const sets = this.buk().get.sets

    return a.filter(
      sets?.accessOrders,
      val => sets.services?.get(val.serviceId)?.originId === id,
    )
  }

  childServices() {
    const buk = this.buk()
    return a.vac(buk) && a.compact(a.arr(this.childServiceOriginIds).map(buk.serviceByOriginOpt, buk))
  }

  parentService() {
    return this.buk().serviceByOrigin(this.parentServiceOriginId)
  }

  siblingServices() {return this.parentService()?.childServices()}

  isSectionCompleted() {
    const id = this.buk().userId()

    return !!id && a.some(
      this.buk().get.sets.personsToServices,
      val => (
        true
        && val.personId === id
        && val.serviceOriginId === this.originId
      )
    )
  }

  ownedByCurrentUser() {return this.personId === this.buk().userId()}

  // TODO better method naming convention.
  static mutInpSets(inp) {
    a.reqInst(inp, BukInp)
    inp.get.sets.services.enable()
    inp.get.sets.serviceExts.enable()
    inp.get.sets.serviceAggs.enable()
    inp.get.sets.persons.enable()
    inp.get.sets.personExts.enable()
    inp.get.sets.personAggs.enable()
    inp.get.sets.files.enable()
  }

  // TODO better method naming convention.
  static mutInpSetsForFeed(inp) {
    a.reqInst(inp, BukInp)
    this.mutInpSets(inp)
    inp.get.sets.countries.enable()
    inp.get.sets.viewMetas.enable()
  }
}

export class BukServices extends u.BukModelColl {get cls() {return BukService}}

export class BukServicePage extends u.BukPage {
  coll() {return this.buk().get.sets?.services}
}

export class BukServicePages extends u.BukPages {get cls() {return BukServicePage}}

export class BukServiceExt extends u.BukModel {
  static spec = {
    ...super.spec,
    ...specMixImaged,
    ...specMixPersoned,
    charge: val => a.toInstOpt(val, CandidateCharge),
    nextAvailableTimespan: val => a.toInstOpt(val, AbsTimespan),
  }
}

export class BukServiceExts extends u.BukModelColl {get cls() {return BukServiceExt}}

export class BukServiceAgg extends u.BukModel {
  static spec = {
    ...super.spec,
    ...specMixCategorized,
  }
  get files() {return a.mapCompact(this.fileIds, id => this.buk().file(id))}
}

export class BukServiceAggs extends u.BukModelColl {get cls() {return BukServiceAgg}}

export class ShortCat extends u.Model {
  static spec = {
    ...super.spec,
    title: a.reqValidStr,
    slug: a.reqValidStr,
  }

  // Used for compatibility between ids and slugs.
  hasKeys(val) {return a.includes(val, this.id) || a.includes(val, this.slug)}

  // Must be used for URLs.
  selBySlug() {return {title: this.title, value: this.slug}}

  // Must be used when creating or updating categorized entities.
  selById() {return {title: this.title, value: this.id}}

  // TODO rename from `.sitePathDefault` to `.sitePath`.
  sitePathDefault() {return this.constructor.sitePathDefault(this.slug)}
  sitePathMain(...val) {return u.url(...val).mainCats(this)}
  sitePathConcern(...val) {return u.url(...val).concernCats(this)}

  static sitePathDefault(...val) {
    return u.url(c.LINK_SERVICES).mainCategorySlugs(val)
  }

  /*
  Used for detecting category IDs that were in use, and possibly indexed by
  search engines, when we changed the route `/articles/:categoryId` to
  something else. This allows to detect and redirect from those routes without
  embedding a huge list of IDs into the application bundle.
  */
  static isKnownOldId(val) {return a.isNat(val) && val >= 1 && val <= 586}

  articleFeedPath() {return u.url(c.LINK_ARTICLES, `category`, this.slug)}

  // Deprecated. Maintained only for URL permanence. Avoid for new URLs.
  static articleFeedPathFromIntId(key) {
    return u.url(c.LINK_ARTICLES, `categories`, key)
  }

  // Detect the old `/articles/:categoryId` route and redirect to the new route.
  static maybeRedirect(url, slug) {
    a.reqInst(url, a.Url)
    const num = u.parseId(slug)

    if (this.isKnownOldId(num)) {
      throw u.errRedir(u.url(
        this.articleFeedPathFromIntId(num),
      ).queryPatch(url.query))
    }
  }
}

export class ShortCats extends u.ModelColl {get cls() {return ShortCat}}

export class Category extends ShortCat {
  static spec = {
    ...super.spec,
    tag: a.reqValidStr,
  }

  #all = undefined
  getAllCats() {return this.#all}
  setAllCats(val) {return this.#all = a.reqInst(val, Categories), this}

  #chi = undefined
  getChildCats() {return this.#chi}
  initChildCats() {return this.#chi ||= []}

  getIsUsable() {return this.isUsable}
  getParentCat() {return this.getAllCats()?.get(this.parentCategoryId)}
  getSiblingCats() {return this.getParentCat()?.getChildCats() || [this]}
  getTotalCount() {return this.recTotalCount}
  isLeaf() {return a.isEmpty(this.getChildCats())}
  isSelectable() {return this.isLeaf() && this.isUsable}
  isMain() {return this.isUnderSlug(c.CATEGORY_SLUG_MAIN)}
  isConcern() {return this.isUnderSlug(c.CATEGORY_SLUG_CONCERN)}

  // Should be ternary and return `undefined` if given slug is missing
  // or if parent is missing.
  isUnderSlug(val) {
    if (!a.optStr(val)) return undefined
    if (val === this.slug) return true
    const parent = this.getParentCat()
    return parent && parent.isUnderSlug(val)
  }

  // TODO rename from `.sitePathDefault` to `.sitePath`.
  sitePathDefault() {
    if (this.isMain()) return this.sitePathMain(c.LINK_SERVICES)
    if (this.isConcern()) return this.sitePathConcern(c.LINK_SERVICES)
    return super.sitePathDefault()
  }

  levels() {
    const buf = CatLevels.of()
    let lvl = CatLevel.of(CatGroup.from(this.getChildCats()))

    while (a.hasLen(lvl)) {
      buf.add(lvl)
      lvl = catLevelToNextLevel(lvl)
    }
    return buf
  }
}

function catLevelToNextLevel(src) {
  return CatLevel.from(a.mapFlat(a.reqInst(src, CatLevel), catGroupToGroups))
}

function catGroupToGroups(src) {
  return a.map(a.reqInst(src, CatGroup), catToGroup)
}

function catToGroup(src) {
  const chi = a.reqInst(src, Category).getChildCats()
  return a.hasLen(chi) ? CatGroup.from(chi) : undefined
}

class CategoryById extends u.MixDedupMap(u.ClsCollByPk) {
  get cls() {return Category}
}

class CategoryBySlug extends u.MixDedupMap(u.ClsCollBySlug) {
  get cls() {return Category}
}

export class Categories extends u.ClsMultiColl_ByIdOrPk_BySlug {
  get cls() {return Category}

  makeIndexes() {return [new CategoryById(), new CategoryBySlug()]}

  mut(src) {return super.mut(src).link()}

  link() {
    for (const val of this) {
      const all = val.getAllCats()
      if (all === this) continue
      if (all) throw Error(`internal error: attempted to link ${a.show(val)} with another parent`)
      val.setAllCats(this)
      val.getParentCat()?.initChildCats().push(val)
    }
    return this
  }

  slugsFromKeys(val) {return a.mapCompact(val, this.slugFromKey.bind(this))}
  slugFromKey(val) {return this.get(val)?.slug}
  titlesFromKeys(val) {return a.map(val, this.titleFromKey.bind(this))}
  titleFromKey(val) {return this.get(val)?.title}
  onlyUsable() {return this.filter(Category.prototype.getIsUsable)}
  onlyLeaves() {return this.filter(Category.prototype.isLeaf)}
  onlySelectable() {return this.filter(Category.prototype.isSelectable)}
  onlyMain() {return this.filter(Category.prototype.isMain)}
  onlyConcern() {return this.filter(Category.prototype.isConcern)}

  // Caution: client-side sorting is an antipattern. This is a rare exception.
  catsMainSimple() {
    return this.onlyMain()
      .toArray()
      .filter(dc.catHasDepthMain)
      .sort(dc.compareByTotalCount)
  }

  onlyKeys(src) {
    src = a.setFrom(src)
    return this.filter(val => val.hasKeys(src))
  }

  selsById() {
    return a.prepend(a.map(this.onlySelectable(), selById), c.CAT_SEL_ALL)
  }

  selsBySlug() {
    return a.prepend(a.map(this.onlySelectable(), selBySlug), c.CAT_SEL_ALL)
  }

  // Generates a patch for the URL query, replacing deprecated category keys
  // with currently supported keys.
  normalizeQuery(val) {
    return {
      mainCategoryIds: undefined,
      concernCategoryIds: undefined,
      mainCategorySlugs: this.mainSlugsFromQuery(val),
      concernCategorySlugs: this.concernSlugsFromQuery(val),
    }
  }

  mainSlugsFromQuery(val) {
    a.reqInst(val, a.Query)
    return a.arr(u.union(
      val.getAll(`mainCategorySlugs`),
      this.slugsFromKeys(val.getAll(`mainCategoryIds`)),
    ))
  }

  concernSlugsFromQuery(val) {
    a.reqInst(val, a.Query)
    return a.arr(u.union(
      val.getAll(`concernCategorySlugs`),
      this.slugsFromKeys(val.getAll(`concernCategoryIds`)),
    ))
  }

  catMain() {return this.get(c.CATEGORY_SLUG_MAIN)}
  catConcern() {return this.get(c.CATEGORY_SLUG_CONCERN)}

  /*
  Must be called EXACTLY once, when constructing `Categories` from data
  fetched from the server. Must NOT be used for smaller filtered sets.
  All categories must retain references to the original full collection.
  */
  initAll() {
    for (const cat of this) {
      cat.setAllCats(this)
      cat.getParentCat()?.initChildCats().push(cat)
    }
    return this
  }

  /*
  Must be computed once and cached. See `dat.mjs`. TODO: either remove this,
  or move this to the server, to support multiple clients.
  */
  conCatMain() {
    const main = this.catMain()
    const tar = ConCat.fromCat(main)

    /*
    Note: if "main" is level 1, then we skip level 2, and use categories
    starting at level 3, grouping them by tag and merging the categories with
    identical tag into one `ConCat`.
    */
    for (const cat of a.values(main.getChildCats())) {
      tar.addChildCatsFrom(cat)
    }
    return tar
  }
}

function selById(val) {return val.selById()}
function selBySlug(val) {return val.selBySlug()}

export class CatLevels extends a.TypedVec {
  reqVal(val) {return a.reqInst(val, CatLevel).setParent(this)}
  // Caution: client-side sorting is an antipattern. This is a rare exception.
  sorted() {return a.each(this, sorted), this}
}

function sorted(val) {return val.sorted()}

class CatTypedVec extends a.MixChild(a.TypedVec) {
  getTotalCount() {return u.sumBy(this, dc.getTotalCount)}
  // Caution: client-side sorting is an antipattern. This is a rare exception.
  sorted() {return this.sort(dc.compareByTotalCount)}
}

export class CatLevel extends CatTypedVec {
  reqVal(val) {return a.reqInst(val, CatGroup).setParent(this)}
  flat() {return a.mapFlat(this, a.values).sort(dc.compareByTotalCount)}
  getDepth() {return a.head(this)?.getDepth()}
  // Caution: client-side sorting is an antipattern. This is a rare exception.
  sorted() {return a.each(this, sorted), super.sorted()}
}

export class CatGroup extends CatTypedVec {
  reqVal(val) {return a.reqInst(val, Category)}
  parentCat() {return a.head(this)?.getParentCat()}
  parentTitle() {return this.parentCat()?.title}
  getDepth() {return a.head(this)?.depth}
}

/*
Short for "concatenation of categories" or "concatenated category". Tool for
grouping categories by tag. Provides shortcuts for recursively merging
categories and their children, converting a tree of `Category` into a tree of
`ConCat`.
*/
export class ConCat extends a.MixChild(u.Model) {
  static spec = {
    ...super.spec,
    title: a.reqValidStr,
    tag: a.reqValidStr,
  }

  static fromCat(val) {
    a.reqInst(val, Category)

    return new this({
      title: val.title,
      tag: val.tag,
    }).addSlug(val.slug)
  }

  slugs = new u.SlugSet()
  getSlugs() {return this.slugs}
  addSlug(val) {return this.slugs.add(val), this}

  #chi = undefined
  getChildConCats() {return this.#chi}
  initChildConCats() {return this.#chi ||= new ConCats().setParent(this)}

  pk() {return this.tag}
  setParent(val) {return super.setParent(a.reqInst(val, this.constructor))}
  addChildCatsFrom(val) {return this.addChildCats(val.getChildCats())}
  addChildCat(val) {return this.initChildConCats().addCat(val), this}
  sitePathDefault() {return Category.sitePathDefault(...this.getSlugs())}

  addCat(val) {
    a.reqInst(val, Category)
    this.addSlug(val.slug)
    this.addChildCatsFrom(this.reqMatchingCat(val))
    return this
  }

  addChildCats(src) {
    for (src of a.values(src)) this.addChildCat(src)
    return this
  }

  reqMatchingCat(val) {
    a.reqInst(val, Category)
    if (val.tag !== this.tag) {
      throw Error(`internal error: tag mismatch between ${a.show(val)} ${a.show(val.tag)} and ${a.show(this)} ${a.show(this.tag)}`)
    }
    return val
  }

  childConCatsByTags(src) {return this.getChildConCats()?.onlyTags(src)}
}

export class ConCats extends a.MixChild(u.ModelColl) {
  get cls() {return ConCat}

  add(val) {return super.add(val.setParent(this.getParent()))}

  addCat(val) {
    a.reqInst(val, Category)
    const key = val.tag

    if (this.has(key)) {
      this.get(key).addCat(val)
    }
    else {
      this.add(this.cls.fromCat(val).addChildCatsFrom(val))
    }
    return this
  }

  onlyTags(src) {
    src = a.setFrom(src)
    return a.filter(this, val => src.has(val.tag))
  }
}

/*
See `ExtTimezone` in Go.

Note: `name` is a standard timezone identifier from the IANA timezone database.
*/
class Timezone extends u.Model {
  static spec = {
    ...super.spec,
    name: a.reqStr,
    abbreviation: a.reqStr,
    utcOffset: a.optFin,
  }

  toString() {
    const {name, abbreviation: abbr, utcOffset} = this
    if (name === `UTC`) return name
    return a.spaced(tzOffset(utcOffset), tzAbbr(abbr), name)
  }
}

function tzOffset(utcOffset) {
  if (!utcOffset) return `(UTC)`
  if (utcOffset < 0) return `(UTC${u.secsToHours(utcOffset)})`
  if (utcOffset > 0) return `(UTC+${u.secsToHours(utcOffset)})`
  return ``
}

function tzAbbr(abbr) {
  if (abbr === `UTC` || abbr === `GMT` || u.isStrNum(abbr)) return ``
  return abbr
}

export class Timezones extends u.ModelColl {get cls() {return Timezone}}

// TODO rename to `DbFile` to avoid collisions with `window.File`.
export class File extends u.Model {
  url(opt) {return u.imageUrl(this, opt)}
  galleryEntry() {return new u.GalleryEntry({name: this.name})}
  sitePath() {return u.maybeUrl(`/api/v2/files`, this.id)}
  guessName() {return this.originalName || this.name}
}

class Files extends u.BukModelColl {get cls() {return File}}

export function toGalleryEntry(src) {return src.galleryEntry()?.valid()}

class ArticleLink extends a.Struct {
  static spec = {
    ...super.spec,
    text: a.laxStr,
    url: a.reqStr,
    query: a.optStruct,
  }
}

class ArticleLinks extends a.ClsVec {get cls() {return ArticleLink}}

export function amount(val) {return a.toInstOpt(val, Amount)}

export class Amount extends a.Emp {
  constructor(src) {
    a.reqStruct(src)
    super()
    this.currency = a.reqStr(src.currency)
    this.cents = a.req(src.cents, Number.isSafeInteger)
  }

  divide(val) {
    this.cents = a.reqFin(this.cents / a.reqFin(val))
    return this
  }

  /*
  Renders in a human-readable fashion: converts cents to whole units, limits the
  amount of decimals, attempts to find the matching currency symbol, may or may
  not display the currency code in addition to the symbol, and so on.

  Beware: multiple currencies may share the same symbol and must be
  disambiguated by currency code. Take dollars for example:

    Canada: symbol = $, code = CAD
    NZ:     symbol = $, code = NZD
    US:     symbol = $, code = USD

  To avoid confusion, we must always display the code. The symbol is secondary.
  There are multiple common formats. We use the first:

    $100.00 USD <-- this
    USD$100.00
    100.00 USD
    USD 100.00

  TODO consider reusing `formatAmount` here.
  */
  toString() {
    return a.spaced(this.formatSymbol() + this.formatCents(), this.formatCurrency())
  }

  formatSymbol() {
    const {currency: val} = this
    return a.laxStr(val && c.CURRENCIES[val.toLowerCase()]?.symbol)
  }

  formatCents() {return u.formatNumber(u.moneyCentsToUnits(this.cents), 2)}

  formatCurrency() {return this.currency.toUpperCase()}

  eq(val) {
    return (
      a.truthy(a.optInst(val, Amount)) &&
      this.currency === val.currency &&
      this.cents === val.cents
    )
  }

  static make(currency, cents) {return new this({currency, cents})}
}

// TODO: verify that timespans are correctly sorted and don't overlap.
export class AbsTimetable extends u.ModelColl {get cls() {return AbsTimespan}}

/*
TODO: use for all absolute timespans. Probably move relevant functions from
`dat-timetable.mjs` into this class.

Go type:

  type AbsTimespan struct {
    StartAt  time.Time `json:"startAt"`
    Duration Seconds   `json:"duration"`
  }
*/
export class AbsTimespan extends u.Model {
  static spec = {
    ...super.spec,
    startAt: u.toValidDate,
    duration: a.reqIntPos,
  }

  pk() {return this.id || (this.key ||= a.uuid())}

  get nextStart() {return new a.DateTime(this.nextStartTimestamp)}
  get startTimestamp() {return this.startAt.valueOf()}
  get nextStartTimestamp() {return this.startTimestamp + u.secsToMillis(this.duration)}
}

class Promotion extends u.BukModel {
  isService() {return this.type === c.PROMOTION_TYPE_SERVICE}
  isArticleLink() {return this.type === c.PROMOTION_TYPE_ARTICLE_LINK}
  isSuper() {return a.hasLen(this.buk()?.promotionRel(this.id)?.promotionIds)}
  isOwned(val) {return val?.promotionId === this.id}
  wares() {return a.arr(this.buk()?.get.sets?.promotionWares).filter(this.isOwned, this)}
  hasWares() {return a.hasLen(this.wares())}
  waresYearly() {return a.filter(this.wares(), isPromotionWareYearly)}
  waresMonthly() {return a.filter(this.wares(), isPromotionWareMonthly)}
  hasTags() {return a.hasLen(this.tags)}
  hasTag(val) {return val ? a.includes(this.tags, val) : !this.hasTags()}
  paymentReason(ctx) {return xln.XLN_PAYMENT_REASON_PROMOTION(ctx)(this.title)}
}

class Promotions extends u.BukModelColl {get cls() {return Promotion}}

// TODO: missing `.gtagOpt` and `.gtagItem`. No GA for this purchase.
class PromotionPurchase extends u.Model {
  mainPromotionWare() {return a.head(this.promotionWares())}
  paymentReason(ctx) {return this.mainPromotionWare()?.paymentReason(ctx)}
}

class BukPromotionPurchase extends u.MixBuketed(PromotionPurchase) {
  promotionWares() {return a.mapCompact(this.buk()?.promotionPurchaseEdges(this.id), edge => this.buk()?.promotionWare(edge.promotionWareId))}
}

class PromotionPurchases extends u.BukModelColl {get cls() {return BukPromotionPurchase}}

export class FinTransfer extends u.BukModel {
  order() {return this.buk()?.order(this.orderId)}
  subscription() {return this.buk()?.subscription(this.subscriptionId)}
  paymentSubject() {return this.buk()?.paymentSubject(this.paymentSubjectId)}
  debitFinAccount() {return this.buk()?.finAccount(this.debitFinAccountId)}
  creditFinAccount() {return this.buk()?.finAccount(this.creditFinAccountId)}

  paymentReason(ctx) {
    return (
      this.paymentSubject()?.paymentReason(ctx) ||
      this.order()?.paymentReason(ctx) ||
      this.subscription()?.paymentReason(ctx) ||
      this.paymentReasonFallback(ctx)
    )
  }

  paymentReasonFallback(ctx) {
    if (this.orderId) return xln.XLN_FIN_TRANSFER_REASON_FALLBACK_ORDER(ctx)
    if (this.giftCardId) return xln.XLN_FIN_TRANSFER_REASON_FALLBACK_GIFT_CARD(ctx)
    if (this.subscriptionId) return xln.XLN_FIN_TRANSFER_REASON_FALLBACK_SUBSCRIPTION(ctx)
    if (this.giftId) return xln.XLN_FIN_TRANSFER_REASON_FALLBACK_GIFT(ctx)
    if (this.personServicePpcId) return xln.XLN_FIN_TRANSFER_REASON_SERVICE_PPC(ctx)
    return undefined
  }

  debitPreview() {return this.debitFinAccount()?.person()?.titlePreview()}
  debitAmount() {return u.formatAmount(this.currency, this.debitCents)}
  creditPreview() {return this.creditFinAccount()?.person()?.titlePreview()}
  creditAmount() {return u.formatAmount(this.currency, this.creditCents)}

  // TODO better method naming convention.
  static mutInpSetsForDetails(inp) {
    inp.get.sets.finTransfers.enable()
    inp.get.sets.orders.enable()
    inp.get.sets.subscriptions.enable()
    inp.get.sets.finAccounts.enable() // For persons.
    inp.get.sets.persons.enable() // For person names.
    PaymentSubject.mutInpSetsForDetails(inp)
  }
}

class FinTransfers extends u.BukModelColl {get cls() {return FinTransfer}}

export class FinTransferPage extends u.BukPage {
  coll() {return this.buk().get.sets?.finTransfers}
}

export class FinTransferPages extends u.BukPages {get cls() {return FinTransferPage}}

class FinAccount extends u.BukModel {
  person() {return this.buk()?.person(this.personId)}
}

class FinAccounts extends u.BukModelColl {get cls() {return FinAccount}}

// TODO: missing `.gtagOpt` and `.gtagItem`. No GA for this purchase.
class DiscountPurchase extends u.Model {}

class BukDiscountPurchase extends u.MixBuketed(DiscountPurchase) {
  discount() {return this.buk()?.discount(this.discountId)}
  paymentReason(ctx) {return this.discount()?.paymentReason(ctx)}
}

class DiscountPurchases extends u.BukModelColl {get cls() {return BukDiscountPurchase}}

class CalAbsTimespan extends u.Model {
  static spec = {
    ...super.spec,
    startAt: val => a.toInstOpt(val, u.DateTime),
    nextStartAt: val => a.toInstOpt(val, u.DateTime),
  }

  intersects(startAt, nextStartAt) {
    return (
      true
      && this.startAt?.valueOf() < u.toValidTimestamp(nextStartAt)
      && this.nextStartAt?.valueOf() > u.toValidTimestamp(startAt)
    )
  }

  isDeleted() {return !!this.deletedAt}
  isUnavailable() {return this.type === c.CAL_ABS_TIMESPAN_TYPE_UNAVAILABLE}
  isDrawable() {return !this.isDeleted() && !this.isUnavailable()}

  get code() {return this.confirmationCode}
  confirmLink() {return calAbsTimespanLink(this, `confirm`)}
  rejectLink() {return calAbsTimespanLink(this, `reject`)}

  dur() {
    const {startAt, nextStartAt} = this
    if (!startAt) return undefined
    if (!nextStartAt) return undefined
    return new u.Dur().resetFromRange(startAt, nextStartAt)
  }
}

function calAbsTimespanLink({id, code}, val) {
  a.reqValidStr(val)
  return u.siteUrl(`/api/v2/cal_abs_timespans`, id, val).setQuery({code})
}

export class BukCalAbsTimespan extends u.MixBuketed(CalAbsTimespan) {
  service() {return this.buk()?.service(this.serviceId)}

  bookings() {
    const id = a.reqPk(this.id)
    const sets = this.buk().get.sets

    return a.filter(
      sets?.bookings,
      val => val.calAbsTimespanId === id
    )
  }
}

export class CalAbsTimespans extends u.BukModelColl {
  get cls() {return BukCalAbsTimespan}
}

export class CalAbsTimespanPage extends u.BukPage {
  coll() {return this.buk().get.sets?.calAbsTimespans}
}

export class CalAbsTimespanPages extends u.BukPages {
  get cls() {return CalAbsTimespanPage}
}

class Discount extends u.BukModel {
  paymentReason(ctx) {return xln.XLN_PAYMENT_REASON_DISCOUNT(ctx)(this.title)}
}

class Discounts extends u.BukModelColl {get cls() {return Discount}}

class AccessOrder extends u.Model {
  paymentReason(ctx) {return this.service?.paymentReason(ctx)}
  gtagOpt() {return this.service?.gtagOpt()}
  gtagItem() {return this.service?.gtagItem()}
}

class ExtAccessOrder extends AccessOrder {
  static spec = {
    ...super.spec,
    person: val => a.toInstOpt(val, AggPerson),
    service: val => a.toInstOpt(val, AggService),
  }
}

class BukAccessOrder extends u.MixBuketed(AccessOrder) {
  get service() {return this.buk()?.service(this.serviceId)}
}

class AccessOrders extends u.BukModelColl {get cls() {return BukAccessOrder}}

class Order extends u.BukModel {
  paymentReason(ctx) {
    switch (this.type) {
      case c.ORDER_TYPE_DEPOSIT:
        return xln.XLN_ORDER_REASON_DEPOSIT(ctx)(this.provider)
      case c.ORDER_TYPE_WITHDRAWAL:
        return xln.XLN_ORDER_REASON_WITHDRAWAL(ctx)(this.provider)
      case c.ORDER_TYPE_REFUND:
        return xln.XLN_ORDER_REASON_REFUND(ctx)(this.provider)
      default:
        return undefined
    }
  }
}

class Orders extends u.BukModelColl {get cls() {return Order}}

class PromotionWare extends u.BukModel {
  static spec = {
    ...super.spec,
    interval: u.durOpt,
    promotion: val => a.toInstOpt(val, Promotion),
  }

  promo() {return this.promotion ?? this.buk()?.promotion(this.promotionId)}
  isSuper() {return this.promo()?.isSuper()}
  siblingWares() {return this.promo()?.wares()}
  priceCentsMonthly() {return this.priceCents / this.interval?.wholeMonths()}
  priceCentsDaily() {return this.priceCents / this.interval?.wholeDays()}
  paymentReason(ctx) {return this.promo()?.paymentReason(ctx)}
}

export class PromotionWares extends u.BukModelColl {get cls() {return PromotionWare}}

export function isPromotionWareYearly(val) {return val.interval?.isYearly()}

export function isPromotionWareMonthly(val) {return val.interval?.isMonthly()}

export function isPromotionWareIntervalOther(val) {
  return !isPromotionWareYearly(val) && !isPromotionWareMonthly(val)
}

export function promotionWareSort(src) {return a.sort(src, promotionWareCompare)}

export function promotionWareCompare(one, two) {
  a.optInst(one, PromotionWare)
  a.optInst(two, PromotionWare)

  if (one?.priceCents < two?.priceCents) return -1
  if (two?.priceCents < one?.priceCents) return 1
  return 0
}

export class Luminary extends u.Model {
  static spec = {
    ...super.spec,
    slug: a.reqValidStr,
    name: a.reqStr,
    content: a.reqStr,
    avatarName: a.optStr,
    tags: optStrs,
  }

  pk() {return this.slug}
}

function optStrs(val) {return a.optArrOf(val, a.isStr)}

class Luminaries extends u.ModelColl {get cls() {return Luminary}}
export class LuminaryPage extends u.ClsPage {get cls() {return Luminaries}}

class ObsModels extends u.ModelColl {get cls() {return u.ObsModel}}
class ObsModelPage extends u.ClsPage {get cls() {return ObsModels}}
export class ObsModelFeed extends u.ClsFeed {get cls() {return ObsModelPage}}

export class Booking extends u.Model {
  gtagOpt() {return this.service?.gtagOpt()}
  gtagItem() {return this.service?.gtagItem()}
}

export class ExtBooking extends Booking {
  static spec = {
    ...super.spec,
    person: val => a.toInstOpt(val, Person),
    service: val => a.toInstOpt(val, AggService),
    calAbsTimespan: val => a.toInstOpt(val, CalAbsTimespan),
  }
}

export class BukBooking extends u.MixBuketed(Booking) {
  get calAbsTimespan() {
    return this.buk()?.get.sets?.calAbsTimespans.get(this.calAbsTimespanId)
  }

  get service() {
    const src = this.buk()?.get.sets?.services
    return src.get(this.serviceId) || src.get(this.calAbsTimespan?.serviceId)
  }

  paymentReason(ctx) {
    return xln.XLN_PAYMENT_REASON_BOOKING(ctx)(
      (this.service || this.calAbsTimespan?.service())?.paymentReason(),
    )
  }
}

class Bookings extends u.BukModelColl {get cls() {return BukBooking}}

export class BookingPage extends u.BukPage {
  coll() {return this.buk().get.sets?.bookings}
}

export class BookingPages extends u.BukPages {get cls() {return BookingPage}}

class PaymentSubject extends u.Model {
  paymentReason(ctx) {
    if (this.depositId) return xln.XLN_PAYMENT_SUBJECT_REASON_DEPOSIT(ctx)

    if (this.giftCardId) {
      return xln.XLN_FIN_TRANSFER_REASON_PURCHASE(ctx)(
        xln.XLN_PAYMENT_SUBJECT_REASON_GIFT_CARD(ctx)
      )
    }

    return (
      this.promotionPurchase?.paymentReason(ctx) ||
      this.discountPurchase?.paymentReason(ctx) ||
      this.booking?.paymentReason(ctx) ||
      this.accessOrder?.paymentReason(ctx)
    )
  }

  anyEnt() {
    return (
      this.deposit ||
      this.booking ||
      this.accessOrder ||
      this.giftCard ||
      this.promotionPurchase ||
      this.discountPurchase
    )
  }

  /*
  For GA purchase-related events. They tend to share the same fields in event
  root and for `items`. References:

    https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#purchase
    https://developers.google.com/tag-platform/gtagjs/reference/events#purchase
  */
  gtagOpt() {
    if (!c.GTAG_ENABLED) return undefined
    const meta = this.meta
    const ent = this.anyEnt()
    const item = ent?.gtagItem?.()

    if (meta && ent && item) {
      return {
        ...a.optDict(ent.gtagOpt?.()),
        currency: u.currencyToGtag(meta.priceCurrency),
        value: u.centsToGtag(meta.priceCents),
        items: [item],
      }
    }

    if (c.PUBLIC_RELEASE) return undefined
    throw Error(`missing gtagOpt in ` + this.constructor.name)
  }

  // TODO better method naming convention.
  static mutInpSetsForDetails(inp) {
    a.reqInst(inp, BukInp)
    inp.get.sets.paymentSubjects.enable()

    // Tables which may be referenced by a payment subject.
    // Should be kept in sync with the DB relations.
    inp.get.sets.bookings.enable()
    inp.get.sets.promotionOrders.enable()
    inp.get.sets.discountPurchases.enable()
    inp.get.sets.accessOrders.enable()
    inp.get.sets.giftCards.enable()
    inp.get.sets.deposits.enable()
    inp.get.sets.promotionPurchases.enable()

    // Stuff referenced by bookings.
    inp.get.sets.services.enable()
    inp.get.sets.calAbsTimespans.enable()
  }
}

class BukPaymentSubject extends u.MixBuketed(PaymentSubject) {
  get discountPurchase() {return this.buk()?.get.sets?.discountPurchases?.get(this.discountPurchaseId)}
  get booking() {return this.buk()?.get.sets?.bookings?.get(this.bookingId)}
  get accessOrder() {return this.buk()?.get.sets?.accessOrders?.get(this.accessOrderId)}
  get promotionPurchase() {return this.buk()?.get.sets?.promotionPurchases?.get(this.promotionPurchaseId)}
}

class PaymentSubjects extends u.BukModelColl {get cls() {return BukPaymentSubject}}

export class ExtPaymentSubjectWithMeta extends PaymentSubject {
  static spec = {
    meta: val => a.toInstOpt(val, PaymentMeta),
    deposit: val => a.toInstOpt(val, Deposit),
    booking: val => a.toInstOpt(val, ExtBooking),
    accessOrder: val => a.toInstOpt(val, ExtAccessOrder),
    giftCard: val => a.toInstOpt(val, GiftCard),
    promotionPurchase: val => a.toInstOpt(val, PromotionPurchase),
    discountPurchase: val => a.toInstOpt(val, DiscountPurchase),
  }
}

class PaymentMeta extends a.StructLax {
  static spec = {
    ...super.spec,
    priceCurrency: a.optStr,
    priceCents: a.reqFin,
    description: a.laxStr,
    balances: a.laxDict,
    opts: val => a.toInstOpt(val, PaymentOpts),
    discountedCharges: val => a.toInstOpt(val, CandidateCharges),
  }
}

class PaymentOpts extends a.StructLax {
  static any(val) {return a.toInstOpt(val, PaymentOpt)}
}

// Mirrors `cs-core` → `PaymentOpt`.
export class PaymentOpt extends a.StructLax {
  static spec = {
    ...super.spec,
    charges: val => a.toInstOpt(val, OptCharges),
  }

  isVisible() {return a.hasLen(this.charges)}
}

// Mirrors `cs-core` → `Charge`.
// TODO convert getters to methods.
export class Charge extends a.StructLax {
  static spec = {
    ...super.spec,
    priceCurrency: a.laxStr,
    priceCents: a.laxFin,
    chargeCents: a.laxFin,
    discountPurchaseId: a.optPk,
  }

  get isFree() {return !this.chargeCents}

  // Reference: `cs-core` → `Charge.DiscountPercent`.
  get discountPercent() {
    return this.priceCents && u.percentInverse(this.chargePercent)
  }

  // Reference: `cs-core` → `Charge.ChargePercent`.
  // TODO how to deal with `NaN`?
  get chargePercent() {
    return 100 * this.chargeCents / this.priceCents
  }

  // Reference: `cs-core` → `Charge.DiscountCents`.
  get discountCents() {
    return this.priceCents - this.chargeCents
  }

  getDefaultChargeCents() {return this.chargeCents}

  priceAmount() {
    return new Amount({currency: this.priceCurrency, cents: this.priceCents})
  }

  chargeAmount() {
    return new Amount({currency: this.priceCurrency, cents: this.chargeCents})
  }

  // Might be invalid if made from `Charge`.
  // Valid on some subclasses such as `SplitCharge`.
  paymentInput(paymentSubjectId) {
    a.reqPk(paymentSubjectId)
    return {paymentSubjectId, charge: this}
  }

  matchesCharge(val) {
    return (
      this.matchesChargeAmount(val) &&
      this.priceCents === val.priceCents &&
      this.discountPurchaseId === val.discountPurchaseId
    )
  }

  matchesChargeAmount(val) {
    a.reqInst(val, Charge)
    return this.chargeAmount().eq(val.chargeAmount())
  }
}

// Mirrors `cs-core` → `CandidateCharge`.
class CandidateCharge extends Charge {
  static spec = {
    ...super.spec,
    isEnabled: a.laxBool,
    note: a.laxStr,
    discountPurchase: a.optStruct,
  }
}

class CandidateCharges extends a.ClsVec {get cls() {return CandidateCharge}}

// Mirrors `cs-core` → `SplitCharge`.
export class SplitCharge extends Charge {
  static spec = {
    ...super.spec,
    internalChargeCents: a.reqFin,
    externalChargeCents: a.reqFin,
  }
}

// Mirrors `cs-core` → `RecurCharge`.
export class RecurCharge extends SplitCharge {
  static spec = {
    ...super.spec,
    recurringInterval: u.durOpt,
    trialChargeCents: a.optFin,
    trialInterval: u.durOpt,
  }

  isRecurrent() {return u.Dur.isValid(this.recurringInterval)}
  hasTrial() {return u.Dur.isValid(this.trialInterval)}
  hasPaidTrial() {return this.hasTrial() && this.trialChargeCents > 0}
  hasFreeTrial() {return this.hasTrial() && this.trialChargeCents === 0}

  priceAmount() {
    return new Amount({currency: this.priceCurrency, cents: this.priceCents})
  }

  trialAmount() {
    const cents = this.trialChargeCents
    return a.vac(cents) && new Amount({currency: this.priceCurrency, cents})
  }
}

// Mirrors `cs-core` → `UiCharge`.
class UiCharge extends RecurCharge {
  static spec = {
    ...super.spec,
    defaultChargeCents: a.reqFin,
  }

  getDefaultChargeCents() {return this.defaultChargeCents}
}

// Mirrors `cs-core` → `OptCharge`.
export class OptCharge extends UiCharge {
  static spec = {
    ...super.spec,
    isEnabled: a.laxBool,
    note: a.laxStr,
    isProfileIncomplete: a.laxBool,
    title: val => a.laxStr(val) || `unknown`,
  }

  // Implement `isSelectable`.
  get value() {return this.discountPurchaseId || this.title}

  isRelevant() {return a.truthy(this.isEnabled || this.note)}
}

class OptCharges extends a.ClsVec {
  get cls() {return OptCharge}

  reqChargeMatch(val) {
    return this.chargeMatch(val) || a.panic(errMatchingCharge())
  }

  chargeMatch(val) {
    a.reqInst(val, Charge)
    for (const elem of this) if (elem.matchesCharge(val)) return elem
    return undefined
  }

  reqChargeMatchAmount(val) {
    return this.chargeMatchAmount(val) || a.panic(errMatchingCharge())
  }

  chargeMatchAmount(val) {
    a.reqInst(val, Charge)
    for (const elem of this) if (elem.matchesChargeAmount(val)) return elem
    return undefined
  }
}

function errMatchingCharge() {return Error(`internal error: no matching charge`)}

export class Balance extends a.Bmap {
  hasBalance() {
    for (const val of this.values()) if (val > 0) return true
    return false
  }

  centsLeftAfter(val) {
    a.reqInst(val, Charge)
    return a.laxFin(this.get(val.priceCurrency)) - a.laxFin(val.chargeCents)
  }

  enoughFor(val) {return this.centsLeftAfter(val) >= 0}
}

export class Subscription extends u.BukModel {
  get paymentSubject() {return this.buk()?.paymentSubject(this.paymentSubjectId)}

  paymentReason(ctx) {
    return xln.XLN_FIN_TRANSFER_REASON_RECURRENT_PAYMENT(ctx)(
      this.paymentSubject?.paymentReason(ctx),
    )
  }
}

export class Subscriptions extends u.BukModelColl {get cls() {return Subscription}}
export class SubscriptionPage extends u.ClsPage {get cls() {return Subscriptions}}

export class BukInp extends u.StructRec {
  static {a.memGet(this)}
  get act() {return new ActInp()}
  get get() {return new GetInp()}

  isValid() {return a.hasLen(this)}
  hasUser() {return this.get.units?.personId}

  userId() {
    this.get.units.personId = true
    return this
  }

  // No aggregates, extensions, or related entities.
  // Should be used when only the base person fields are needed.
  userBasic() {
    this.userId()
    this.get.sets.persons.enable()
    return this
  }

  user() {
    this.userId()
    this.get.units.person = true
    this.get.units.personPrivateMeta = true
    return this
  }

  practitionerProgress() {
    // Person ID is necessary because it allows the buket API to find
    // practitioner progress for the current user.
    this.get.units.personId = true
    this.get.sets.practitionerProgress.enable()
  }

  aggService(id) {
    this.get.sets.services.enable()
    this.get.sets.services.add(id)
    this.get.sets.files.enable()

    // Required for charge information.
    this.get.sets.serviceExts.enable()

    // Agg is required for `BukService..childServiceOriginIds`.
    this.get.sets.serviceAggs.enable()

    return this
  }
}

export class BukOut extends u.StructRec {
  static {a.memGet(this)}
  get get() {return new GetOut()}

  constructor(src) {
    super()
    this.mut(src)
    return ob.obs(this)
  }

  /*
  Adds the following features:

    * While mutating, delay observable notifications. When done, flush any
      scheduled observable triggers. This avoids redundant triggers which could
      be caused by multiple mutations of the same observable. This also ensures
      that when triggers run, the buket state has already been fully updated.

    * While mutating, any objects using `bukDyn` receive the current buket
      instance. This is used by `MixBuketed` and `BukModel`. Access to the
      buket allows models to reference other models in the same buket.
  */
  mut(src) {
    ob.Sched.main.pause()
    try {
      const prev = u.bukDyn.swap(this)
      try {return super.mut(src)}
      finally {u.bukDyn.swap(prev)}
    }
    finally {
      ob.Sched.main.resume()
    }
  }

  userIdReq() {return this.userId() ?? a.panic(dmi.errUnauth())}
  userId() {return this.get.units?.personId || this.get.units?.person?.id}
  userReq() {return this.userOpt() ?? a.panic(dmi.errUnauth())}
  userOpt() {return this.get.units?.person || this.person(this.userId())}
  person(key) {return this.get.sets?.persons?.get(key)}
  personExt(key) {return this.get.sets?.personExts?.get(key)}
  personAgg(key) {return this.get.sets?.personAggs?.get(key)}
  msg(key) {return this.get.sets?.msgs?.get(key)}
  msgAgg(key) {return this.get.sets?.msgAggs?.get(key)}
  markAgg(key) {return this.get.sets?.markAggs?.get(key)}
  userMarkAgg(key) {return this.get.sets?.userMarkAggs?.get(key)}
  markPairs(key) {return this.markAgg(key)?.markPairs}
  userEmojiChars(key) {return this.userMarkAgg(key)?.emojiChars}
  chan(key) {return this.get.sets?.chans?.get(key)}
  article(key) {return this.get.sets?.articles?.get(key)}
  articleExt(key) {return this.get.sets?.articleExts?.get(key)}
  articleAgg(key) {return this.get.sets?.articleAggs?.get(key)}
  service(key) {return this.get.sets?.services?.get(key)}
  serviceByOrigin(key) {return this.get.sets?.services?.getByOrigin(key)}
  serviceByOriginOpt(key) {return this.get.sets?.services?.getByOriginOpt(key)}
  serviceExt(key) {return this.get.sets?.serviceExts?.get(key)}
  serviceAgg(key) {return this.get.sets?.serviceAggs?.get(key)}
  viewMeta(key) {return this.get.sets?.viewMetas?.getOpt(key)}
  ent(key) {return this.get.sets?.ents?.get(key)}
  file(key) {return this.get.sets?.files?.get(key)}
  category(key) {return this.get.sets?.categories?.get(key)}
  promotion(key) {return this.get.sets?.promotions?.get(key)}
  promotionWare(key) {return this.get.sets?.promotionWares?.get(key)}
  promotionRel(key) {return this.get.sets?.promotionRels?.get(key)}
  mainPagePromotedServices(key) {return this.get.sets?.mainPagePromotedServices?.get(key)}
  finTransfer(key) {return this.get.sets?.finTransfers?.get(key)}
  paymentSubject(key) {return this.get.sets?.paymentSubjects?.get(key)}
  order(key) {return this.get.sets?.orders?.get(key)}
  subscription(key) {return this.get.sets?.subscriptions?.get(key)}
  discount(key) {return this.get.sets?.discounts?.get(key)}
  finTransferPage(key) {return this.get.pages?.finTransfers?.get(key)}
  finAccount(key) {return this.get.sets?.finAccounts?.get(key)}
  calRelTimespan(key) {return this.get.sets?.calRelTimespans?.get(key)}
  calAbsTimespan(key) {return this.get.sets?.calAbsTimespans?.get(key)}
  practitionerProgress() {return this.get.sets?.practitionerProgress?.get(this.userId())}
  country(key) {return this.get.sets?.countries?.get(key)}
  review(key) {return this.get.sets?.reviews?.get(key)}
  personReview(key) {return this.get.sets?.personReviews?.get(key)}
  serviceReview(key) {return this.get.sets?.serviceReviews?.get(key)}
  booking(key) {return this.get.sets?.bookings?.get(key)}
  receipt(key) {return this.get.sets?.receipts?.get(key)}

  // Must not be called `.act` to avoid collision with the field `.act`.
  getAct(key) {return this.get.sets?.acts?.get(key)}

  promotionPurchaseEdges(id) {
    return a.filter(
      this.get.sets?.promotionPurchaseEdges,
      edge => edge.promotionPurchaseId === id,
    )
  }

  promoBySlug(key) {
    return (
      this.promoBySlugOpt(key) ??
      a.panic(u.errNotFound(`missing promo ${a.show(key)}`))
    )
  }

  promoBySlugOpt(key) {
    return a.find(this.get.sets?.promos, val => val.slug === key)
  }

  personPromoByPromoId(id) {
    return a.find(this.get.sets?.personPromos, val => val.promoId === id)
  }
}

export class ActInpChanMetas extends u.StructRec {
  subscribe(chanId) {return this.mut({upsert: [{chanId, subscribedAt: new Date()}]})}
  unsubscribe(chanId) {return this.mut({upsert: [{chanId, subscribedAt: null}]})}
  // TODO: Will be better to set the date of the last read message.
  read(chanId, type = `public`) {return this.mut({upsert: [{chanId, type, readAt: new Date()}]})}
  readNotif(chanId) {return this.read(chanId, c.MSG_TYPE_NOTIF)}
}

export class ActInp extends u.StructRec {
  static {a.memGet(this)}
  get msgs() {return new u.StructRec()}
  get pref() {return new u.StructRec()}
  get chanMetas() {return new ActInpChanMetas()}
}

export class GetInp extends u.StructRec {
  static {a.memGet(this)}
  get pages() {return new PageInp()}
  get units() {return new UnitInp()}
  get sets() {return new SetInp()}
  get meta() {return new u.StructRec()}
}

export class GetOut extends u.StructRec {
  static spec = {
    ...super.spec,
    pages: val => a.toInstOpt(val, PageOut),
    units: val => a.toInstOpt(val, UnitOut),
    sets: val => a.toInstOpt(val, SetOut),
  }
}

export class PageInp extends a.StructLax {
  static any(val) {return a.toInst(val, this.any)}

  static {a.memGet(this)}
  get articleFilters() {return new BukPageInps()}
  get articles() {return new BukPageInps()}
  get bookings() {return new BukPageInps()}
  get calAbsTimespans() {return new BukPageInps()}
  get calRelTimespans() {return new BukPageInps()}
  get finTransfers() {return new BukPageInps()}
  get mainPagePromotedServices() {return new BukPageInps()}
  get msgs() {return new BukPageInps()}
  get msgServices() {return new BukPageInps()}
  get persons() {return new BukPageInps()}
  get promotionWares() {return new BukPageInps()}
  get reviews() {return new BukPageInps()} // Unused, TODO use.
  get serviceFilters() {return new BukPageInps()}
  get services() {return new BukPageInps()}
  get stripePaymentMethods() {return new BukPageInps()}
  get acts() {return new BukPageInps()}
  get receipts() {return new BukPageInps()}

  addMsgsWithServices(inp) {
    const key = a.reqPk(a.reqStruct(inp).key)
    this.msgs.add(inp)
    this.msgServices.add({key, msgPageKey: key})
  }
}

export class PageOut extends a.StructLax {
  static any(val) {return  a.toInst(val, u.BukPages)}

  static spec = {
    ...super.spec,
    articles: val => a.toInstOpt(val, BukArticlePages),
    services: val => a.toInstOpt(val, BukServicePages),
    bookings: val => a.toInstOpt(val, BookingPages),
    mainPagePromotedServices: val => a.toInstOpt(val, BukServicePages),
    msgServices: val => a.toInstOpt(val, BukServicePages),
    serviceFilters: val => a.toInstOpt(val, UiFormColl),
    articleFilters: val => a.toInstOpt(val, UiFormColl),
    finTransfers: val => a.toInstOpt(val, FinTransferPages),
    calRelTimespans: val => a.toInstOpt(val, CalRelTimespanPages),
    calAbsTimespans: val => a.toInstOpt(val, CalAbsTimespanPages),
    reviews: val => a.toInstOpt(val, ReviewPages), // Unused, TODO fix.
    stripePaymentMethods: val => a.toInstOpt(val, StripePaymentMethodPages),
    acts: val => a.toInstOpt(val, ActPages),
    receipts: val => a.toInstOpt(val, ReceiptPages),
  }
}

export class BukPageInps extends u.ModelColl {
  get cls() {return BukPageInp}

  // See comment on `c.CONTENT_TYPE_JSON_QUERY`.
  addQuery(src) {
    return this.add({...a.optDict(src), contentType: c.CONTENT_TYPE_JSON_QUERY})
  }
}

export class BukPageInp extends u.Model {
  pk() {return this.key}

  // See comment on `c.CONTENT_TYPE_JSON_QUERY`.
  toJSON() {
    if (this.contentType === c.CONTENT_TYPE_JSON_QUERY) {
      return u.queryToDictAll(this)
    }
    return this
  }
}

export class MsgPageInp extends BukPageInp {
  /*
  WTF! Our msg filtering and ordering API causes this complexity.
  TODO consider simplifying the API to make this unnecessary.
  */
  align({desc, from, to, exact}) {
    a.reqBool(desc)
    a.optScalar(from)
    a.optScalar(to)
    a.optBool(exact)

    this.orderings = [a.spaced(`createdAt`, u.descToDir(desc))]

    this.and = u.vacOf(
      a.vac(from) && {createdAt: {
        value: from,
        op: (
          (!desc && exact) ? `>=` :
          (desc && exact) ? `<=` :
          (!desc && !exact) ? `>` :
          (desc && !exact) ? `<` :
          a.panic(u.errUnreachable())
        ),
      }},
      a.vac(to) && {createdAt: {
        value: to,
        op: (
          (!desc && exact) ? `<=` :
          (desc && exact) ? `>=` :
          (!desc && !exact) ? `<` :
          (desc && !exact) ? `>` :
          a.panic(u.errUnreachable())
        ),
      }},
    )
    return this
  }
}

export class UnitInp extends u.StructRec {
  static {a.memGet(this)}
  get forms() {return new u.StructRec()}
}

export class UnitOut extends u.StructRec {
  static spec = {
    ...super.spec,
    pref: val => a.toInstOpt(val, AggPref),
    forms: val => a.toInstOpt(val, UiFormStruct),
    person: val => a.toInstOpt(val, AggPerson),
    balance: val => a.toInstOpt(val, Balance),
    followedChanIds: a.optArr,
    ownedChanMsgStatAggs: a.optArr,
    servicePpcMeta: val => a.toInstOpt(val, ServicePpcMeta),
  }
}

export class SetInp extends u.StructRec {
  static {a.memGet(this)}

  get articles() {return new BukSetInp()}
  get articleExts() {return new BukSetInp()}
  get articleAggs() {return new BukSetInp()}
  get bookings() {return new BukSetInp()}
  get calAbsTimespans() {return new BukSetInp()}
  get calRelTimespans() {return new BukSetInp()}
  get categories() {return new BukSetInp()}
  get chans() {return new BukSetInp()}
  get countries() {return new BukSetInp()}
  get viewMetas() {return new BukSetInp()}
  get emoji() {return new BukSetInp()}
  get files() {return new BukSetInp()}
  get finTransfers() {return new BukSetInp()}
  get idents() {return new BukSetInp()}
  get markAggs() {return new BukSetInp()}
  get msgAggs() {return new BukSetInp()}
  get msgs() {return new BukSetInp()}
  get persons() {return new BukSetInp()}
  get personExts() {return new BukSetInp()}
  get personAggs() {return new BukSetInp()}
  get practitionerProgress() {return new BukSetInp()}
  get promotionRels() {return new BukSetInp()}
  get promotions() {return new BukSetInp()}
  get promotionWares() {return new BukSetInp()}
  get services() {return new BukSetInp()}
  get serviceExts() {return new BukSetInp()}
  get serviceAggs() {return new BukSetInp()}
  get userMarkAggs() {return new BukSetInp()}
  get reviews() {return new BukSetInp()}
  get personReviews() {return new BukSetInp()}
  get personReviewAggs() {return new BukSetInp()}
  get serviceReviews() {return new BukSetInp()}
  get serviceReviewAggs() {return new BukSetInp()}
  get personsToServices() {return new BukSetInp()}
  get accessOrders() {return new BukSetInp()}
  get promos() {return new BukSetInp()}
  get personPromos() {return new BukSetInp()}
  get personServicePpcs() {return new BukSetInp()}
  get personArticleLinks() {return new BukSetInp()}
  get acts() {return new BukSetInp()}
  get ents() {return new BukSetInp()}
  get orders() {return new BukSetInp()}
  get paymentSubjects() {return new BukSetInp()}
  get giftCards() {return new BukSetInp()}
  get subscriptions() {return new BukSetInp()}
  get gifts() {return new BukSetInp()}
  get promotionOrders() {return new BukSetInp()}
  get discountPurchases() {return new BukSetInp()}
  get deposits() {return new BukSetInp()}
  get promotionPurchases() {return new BukSetInp()}
  get finAccounts() {return new BukSetInp()}
  get reactionMetas() {return new BukSetInp()}
}

export class SetOut extends a.StructLax {
  static any(val) {return a.toInstOpt(val, u.ModelColl)}

  static spec = {
    ...super.spec,
    accessOrders: val => a.toInstOpt(val, AccessOrders),
    articleAggs: val => a.toInstOpt(val, BukArticleAggs),
    articleExts: val => a.toInstOpt(val, BukArticleExts),
    articles: val => a.toInstOpt(val, BukArticles),
    bookings: val => a.toInstOpt(val, Bookings),
    calAbsTimespans: val => a.toInstOpt(val, CalAbsTimespans),
    calRelTimespans: val => a.toInstOpt(val, CalRelTimespans),
    categories: val => a.toInstOpt(val, Categories),
    countries: val => a.toInstOpt(val, Countries),
    viewMetas: val => a.toInstOpt(val, ViewMetas),
    discountPurchases: val => a.toInstOpt(val, DiscountPurchases),
    discounts: val => a.toInstOpt(val, Discounts),
    emoji: val => a.toInstOpt(val, EmojiColl),
    ents: val => a.toInstOpt(val, Ents),
    facebookAccounts: val => a.toInstOpt(val, FacebookAccounts),
    files: val => a.toInstOpt(val, Files),
    finAccounts: val => a.toInstOpt(val, FinAccounts),
    finTransfers: val => a.toInstOpt(val, FinTransfers),
    idents: val => a.toInstOpt(val, Idents),
    instagramBusinessAccounts: val => a.toInstOpt(val, InstagramBusinessAccounts),
    markAggs: val => a.toInstOpt(val, MarkAggs),
    msgs: val => a.toInstOpt(val, Msgs),
    msgServices: val => a.toInstOpt(val, BukServices),
    chanMetas: val => a.toInstOpt(val, ChanMetas),
    orders: val => a.toInstOpt(val, Orders),
    paymentSubjects: val => a.toInstOpt(val, PaymentSubjects),
    persons: val => a.toInstOpt(val, BukPersons),
    promotionPurchaseEdges: val => a.optArr(val),
    promotionPurchases: val => a.toInstOpt(val, PromotionPurchases),
    promotions: val => a.toInstOpt(val, Promotions),
    promotionWares: val => a.toInstOpt(val, PromotionWares),
    serviceAggs: val => a.toInstOpt(val, BukServiceAggs),
    serviceExts: val => a.toInstOpt(val, BukServiceExts),
    services: val => a.toInstOpt(val, BukServices),
    subscriptions: val => a.toInstOpt(val, Subscriptions),
    userMarkAggs: val => a.toInstOpt(val, UserMarkAggs),
    reviews: val => a.toInstOpt(val, Reviews),
    personReviews: val => a.toInstOpt(val, PersonReviews),
    personReviewAggs: val => a.toInstOpt(val, PersonReviewAggs),
    serviceReviews: val => a.toInstOpt(val, ServiceReviews),
    serviceReviewAggs: val => a.toInstOpt(val, ServiceReviewAggs),
    personsToServices: a.optArr,
    stripePaymentMethods: val => a.toInstOpt(val, StripePaymentMethods),
    acts: val => a.toInstOpt(val, Acts),
    receipts: val => a.toInstOpt(val, Receipts),
  }
}

class BukSetInp extends a.StructLax {
  enable() {return this.isEnabled = true, this}

  add(id) {
    this.enable()
    this.ids = a.append(this.ids, a.reqPk(id))
    return this
  }
}

// TODO: does this need to be observable? Probably not. Drop the proxy.
export class Msg extends u.BukObsModel {
  author() {return this.buk()?.person(this.personId)}
  agg() {return this.buk()?.msgAgg(this.id)}
  chan() {return this.buk()?.chan(this.chanId)}

  subj() {
    const buk = this.buk()
    const chan = buk?.chan(this.chanId)

    // SYNC[msg_chan_types].
    return chan && (
      buk.msg(chan.msgId) ||
      buk.person(chan.personId) ||
      buk.article(chan.articleOriginId) ||
      buk.service(chan.serviceOriginId)
    )
  }

  // Buket must contain current person. See `BukInp..user`.
  canControl() {
    return !!(
      u.teq(this.personId, this.buk()?.userId()) ||
      this.buk()?.userOpt()?.isModerator
    )
  }

  hasComments() {return this.agg()?.commentCount > 0}

  titlePreview() {return u.mdPreviewShort(this.content)}

  // Requires additional support from `MsgThread`. It must be able to render an
  // ARBITRARY chan, such as person/article/service/msg. The current
  // implementation assumes that the chan is a `Msg`.
  chanPath() {
    if (this.chanId === c.GLOBAL_CHAN_ID) return `/msgs`
    return u.url(`/msgs`, this.chanId)
  }

  isPersonalChannel() {
    return this.personId === this.chan()?.personId
  }

  // TODO XLN.
  subjType() {
    const chan = this.chan()
    if (chan?.msgId) return `reply to`
    return ` • `
  }

  subjPagePath() {
    if (this.chanId === c.GLOBAL_CHAN_ID) return `/msgs`
    return this.subj()?.sitePath?.()
  }

  subjTitle() {
    if (this.chanId === c.GLOBAL_CHAN_ID) return `in global channel`
    return this.subj()?.titlePreview?.() || `in unknown thread`
  }

  static sitePath(id) {return u.url(`/msgs`, id)}

  // TODO better method naming convention.
  static mutInpSets(inp) {
    a.reqInst(inp, BukInp)

    /*
    Enabling these sets allows to preview chan titles for msgs which are
    comments or replies.

    SYNC[msg_chan_types].
    */
    inp.get.sets.msgs.enable()
    inp.get.sets.persons.enable()
    inp.get.sets.articles.enable()
    inp.get.sets.services.enable()

    // For author images.
    inp.get.sets.files.enable()
  }
}

export class Msgs extends u.BukModelColl {
  get cls() {return Msg}

  ancestorIds(id) {
    const buf = []
    while (this.has(a.optPk(id))) {
      buf.push(id)
      id = this.get(id).chanId
    }
    return buf
  }
}

export class ChanMeta extends u.Model {
  pk() {return this.chanKey}
}

export class ChanMetas extends u.ModelColl {
  get cls() {return ChanMeta}
}

// TODO: does this need to be observable? Probably not. Drop the proxy.
export class MarkAgg extends u.BukObsModel {
  static spec = {
    ...super.spec,
    markPairs: val => a.toInstOpt(val, MarkPairs),
  }
}

class MarkAggs extends u.BukModelColl {get cls() {return MarkAgg}}

export class IMarkPair extends u.Model {
  pk() {return this.emojiChar}

  static validate(tar) {
    u.reqEmojiChar(tar.emojiChar)
    a.reqFin(tar.count)
  }
}

/*
Server data must be a list of tuples:

  [["🔥", 123], ["🚀", 234], ...]
*/
export class MarkPairs extends u.ResetModelColl {
  get cls() {return MarkPair}
  chars() {return a.map(this, a.pk)}
}

/*
Server data is expected to be a tuple, for example:

  ["🔥", 123]
*/
export class MarkPair extends IMarkPair {
  constructor(src) {super(toMarkPair(src))}
  mut(src) {return super.mut(toMarkPair(src))}
}

function toMarkPair(src) {
  if (a.isArr(src)) return {emojiChar: src[0], count: src[1]}
  return src
}

// TODO: does this need to be observable? Probably not. Drop the proxy.
export class UserMarkAgg extends u.BukObsModel {
  static spec = {
    ...super.spec,
    emojiChars: val => a.toInstOpt(val, u.EmojiCharSet),
  }
}

class UserMarkAggs extends u.BukModelColl {get cls() {return UserMarkAgg}}

/*
Server data must look like this:

  {"id": "🔥"}
  {"id": "🚀"}
  ...
*/
export class Emoji extends IMarkPair {
  get emojiChar() {return this.id}
  get count() {return 0}
}

export class EmojiColl extends u.ResetModelColl {
  get cls() {return Emoji}
  chars() {return a.map(this, a.pk)}
}

class Ent extends u.BukModel {
  personOpt() {return this.buk()?.person(this.id)}
  articleOpt() {return this.buk()?.get?.sets?.articles?.getOpt(this.id)}
  serviceOpt() {return this.buk()?.get?.sets?.services?.getOpt(this.id)}
  msgOpt() {return this.buk()?.get?.sets?.msgs?.getOpt(this.id)}
}

class Ents extends u.BukModelColl {get cls() {return Ent}}

export class Pref extends a.Struct {
  static spec = {
    ...super.spec,
    isIgnored:            a.optBool,
    isEmailPublic:        a.optBool,
    isPhonePublic:        a.optBool,
    isAutoBillingEnabled: a.optBool,
  }

  // Suboptimal but easy.
  isValid() {return !!a.vac(a.values(this))}
}

export class AggPref extends Pref {
  static spec = {
    ...super.spec,
    mainCategoryIds:    u.optValidStrArr,
    concernCategoryIds: u.optValidStrArr,
  }
}

export class UiForm extends a.StructLax {
  static spec = {
    ...super.spec,
    key: a.optStr,
    submit: val => a.toInstOpt(val, UiSubmit),
    fields: val => a.toInstOpt(val, UiFields),
  }

  pk() {return this.key}

  // Converts server-defined field specs to the form spec defined and
  // understood by `util-form.mjs`.
  formSpec() {return u.serverFieldSpecsToLegacyFormSpec(this.fields)}

  withOnlyFields(...keys) {
    const tar = new this.constructor(this)
    tar.fields = tar.fields?.withOnlyFields(...keys)
    return tar
  }

  /*
  Tentative. Takes an entity, or in other words any struct of JS values,
  the kind that would be collected from a form for a JSON endpoint,
  and merges those values into known fields with matching names.
  */
  mutVals(src) {
    if (a.isNil(src)) return this
    a.reqStruct(src)
    for (const field of this.fields) {
      const name = field.name
      if (!a.isValidStr(name)) continue
      if (!a.hasOwnEnum(src, name)) continue
      field.value = src[name]
    }
    return this
  }
}

export class UiFormStruct extends a.StructLax {
  static any(val) {return a.toInstOpt(val, UiForm)}
}

export class UiFormColl extends u.ModelColl {get cls() {return UiForm}}

export class UiSubmit extends a.StructLax {
  static spec = {
    ...super.spec,
    method: a.optStr,
    path: a.optStr,
  }
}

export class UiField extends a.Struct {
  static spec = {
    ...super.spec,
    name: a.optStr,
    type: a.optStr,
    title: a.optStr,
    desc: a.optStruct,
    multiple: a.optBool,
    validation: a.optStruct,
    groupTitle: a.optStr,
    placeholder: a.optStr,
    value: a.optSome,
    selectables: val => a.toInstOpt(val, UiSelectables),
    selectableEndpoint: val => a.toInstOpt(val, UiSelectableEndpoint),
    partitions: u.optStrArr,
    hidden: a.optBool,
    optional: a.optBool,
    related: a.optSome,
  }

  pk() {return this.name}
  getTitle() {return this.title || this.name}
  getTitleLong() {return this.getDescText() || this.getTitle()}
  getDescText() {return this.desc?.type === c.UI_VIEW_TYPE_TEXT ? a.laxStr(this.desc.value) : ``}

  /*
  The fields `.multiple` and `.value` are correlated.
  When `.multiple = true`, `.value` should be a list.
  Otherwise, `.value` is arbitrary.
  */
  getValueSingle() {
    if (this.multiple) return a.head(this.value)
    return this.value
  }
}

export class UiFields extends a.ClsColl {
  get cls() {return UiField}

  withOnlyFields(...keys) {
    u.reqStrArr(keys)
    return new this.constructor(a.filter(this, function testFieldKey(val) {
      return a.includes(keys, a.pk(val))
    }))
  }
}

/*
TODO: validate that either value or title was provided.
If neither was provided, the input selectable was invalid.

Somewhat redundant with `isSelectable` and `isValidSelectable`.
TODO dedup if possible.
*/
export class UiSelectable extends a.Struct {
  static spec = {
    ...super.spec,
    value: a.optSome,
    title: a.optStr,
  }

  pk() {return this.value}
}

export class UiSelectables extends a.ClsVec {get cls() {return UiSelectable}}

export class UiSelectableEndpoint extends a.Struct {
  static spec = {
    ...super.spec,
    path: a.reqValidStr,
    method: a.optStr,
    type: a.reqValidStr,
    filters: val => a.toInstOpt(val, UiFields),
    titleKey: a.optStr,
    valueKey: a.optStr,
    imageKey: a.optStr,
    filterKey: a.optStr,
  }
}

export class Review extends u.BukModel {
  personReview() {return this.buk()?.personReview(this.personReviewId)}
  serviceReview() {return this.buk()?.serviceReview(this.serviceReviewId)}
}

export class Reviews extends u.ModelColl {get cls() {return Review}}

export class ReviewPage extends u.BukPage {
  /*
  Note: this field is present in the review page returned by the old non-buket
  API. In the buket API, review stats have been moved to review aggregates such
  as `ServiceReviewAgg`. This field is defined here because this class is used
  for both APIs.
  */
  static spec = {
    ...super.spec,
    reviewStats: a.optStruct,
  }

  coll() {return this.buk().get.sets?.reviews}
}

export class ReviewPages extends u.BukPages {get cls() {return ReviewPage}}

// Deprecated, TODO convert our review-related code to buket API.
export class ReviewFeed extends u.ClsFeed {get cls() {return ReviewPage}}

const specMixReviewAgg = {
  count: a.reqInt,
  hasOwn: a.reqBool,
}

const specMixPersonReviewRating = {
  accuracy: a.reqFin,
  professionalism: a.reqFin,
  decency: a.reqFin,
  sociability: a.reqFin,
  cleanliness: a.reqFin,
  overall: a.reqFin,
}

export class PersonReview extends u.Model {
  static spec = {
    ...super.spec,
    ...specMixPersonReviewRating,
  }
}

export class PersonReviews extends u.ModelColl {get cls() {return PersonReview}}

export class PersonReviewAgg extends PersonReview {
  static spec = {
    ...super.spec,
    ...specMixReviewAgg,
  }
}

export class PersonReviewAggs extends u.ModelColl {get cls() {return PersonReviewAgg}}

const specMixServiceReviewRating = {
  expertise: a.reqFin,
  socialSkills: a.reqFin,
  servicePrice: a.reqFin,
  accuracy: a.reqFin,
  effectiveness: a.reqFin,
  overall: a.reqFin,
}

export class ServiceReview extends u.Model {
  static spec = {
    ...super.spec,
    ...specMixServiceReviewRating,
  }
}

export class ServiceReviews extends u.ModelColl {get cls() {return ServiceReview}}

export class ServiceReviewAgg extends ServiceReview {
  static spec = {
    ...super.spec,
    ...specMixReviewAgg,
  }
}

export class ServiceReviewAggs extends u.ModelColl {get cls() {return ServiceReviewAgg}}

export class FacebookAccountBase extends u.Model {
  getName() {throw u.errImplementInSubclass()}
}

export class FacebookAccount extends FacebookAccountBase {
  static spec = {
    ...super.spec,
    id: a.reqValidStr,
    name: a.reqValidStr,
    instagramBusinessAccount: a.optStruct,
  }

  getName() {return this.name}
}

export class FacebookAccounts extends a.ClsColl {
  get cls() {return FacebookAccount}
}

export class InstagramBusinessAccount extends FacebookAccountBase {
  static spec = {
    ...super.spec,
    id: a.reqValidStr,
    username: a.reqValidStr,
    profilePictureUrl: a.optStr,
  }

  getName() {return this.username}
}

export class InstagramBusinessAccounts extends a.ClsColl {
  get cls() {return InstagramBusinessAccount}
}

class Ident extends u.Model {
  static spec = {
    ...super.spec,
    ...specMixTimed,
    id: a.reqValidStr,
    provider: a.reqValidStr,
    oauthExpiry: val => a.toInstOpt(val, a.DateValid),
  }
}

export class Idents extends a.ClsVec {
  get cls() {return Ident}

  // Suboptimal but suitable for our purposes because buket includes only
  // account idents for current person, which should be scarce.
  has(prov) {return a.some(this, val => val.provider === prov)}
}

export class SocialProvider extends u.Model {
  static spec = {
    ...super.spec,
    prov: a.reqValidStr,
    title: a.reqValidStr,
    authPath: a.reqValidStr,
    isConnected: a.optBool,
  }

  pk() {return this.prov}

  // TODO: "publish" should be optional.
  authLink(ctx) {
    const url = a.reqInst(ctx.url, a.Url)

    return u.url(this.authPath).setQuery({
      redir: u.siteUrl(url.pathname),
      isAdd: true,
      isPublish: true,
    })
  }
}

export class SocialProviders extends u.ModelColl {
  get cls() {return SocialProvider}

  static preinit(ctx) {
    ctx.pageDat.inp.get.sets.idents.enable()
  }

  // TODO this should come from backend.
  static make(buk) {
    a.reqInst(buk, BukOut)
    const idents = buk.get.sets?.idents

    return new this(a.compact([
      {
        prov: c.IDENT_PROVIDER_GOOGLE,
        title: `Google`,
        authPath: c.OAUTH_PATH_GOOGLE,
        isConnected: idents?.has(c.IDENT_PROVIDER_GOOGLE),
      },
      {
        prov: c.IDENT_PROVIDER_FACEBOOK,
        title: `Facebook`,
        authPath: c.OAUTH_PATH_FACEBOOK,
        isConnected: idents?.has(c.IDENT_PROVIDER_FACEBOOK),
      },
      a.vac(c.FEAT_NEW) && {
        prov: c.IDENT_PROVIDER_ETSY,
        title: `Etsy`,
        authPath: c.OAUTH_PATH_ETSY,
        isConnected: idents?.has(c.IDENT_PROVIDER_ETSY),
      },
    ]))
  }
}

class Endpoint extends a.Struct {
  static spec = {
    ...super.spec,
    url: a.reqValidStr,
    method: a.optValidStr,
    body: a.optObj,
  }

  reqOpt() {
    const {url, method, body} = this
    if (a.isNil(body)) return {url, method}

    return {
      url,
      method: this.method,
      headers: [a.HEADER_JSON],
      body: a.jsonEncode(body),
    }
  }
}

export class PublishEndpoint extends Endpoint {
  static spec = {
    ...super.spec,
    prov: a.reqValidStr,
    title: a.reqValidStr,
  }

  get method() {return a.POST}
}

export class PublishEndpoints extends a.ClsVec {
  get cls() {return PublishEndpoint}
}

export class CalRelTimespan extends u.Model {
  static spec = {
    ...super.spec,
    weekday: a.reqValidStr,
    start: a.reqNat,
    duration: a.reqIntPos,
    nextStart: a.reqIntPos,
  }
}

export class CalRelTimespans extends u.BukModelColl {
  get cls() {return CalRelTimespan}
}

export class CalRelTimespanPage extends u.BukPage {
  coll() {return this.buk().get.sets?.calRelTimespans}
}

export class CalRelTimespanPages extends u.BukPages {
  get cls() {return CalRelTimespanPage}
}

class Country extends u.Model {
  static spec = {
    ...super.spec,
    code: a.reqValidStr,
    name: a.reqValidStr,
  }

  pk() {return this.code}
}

class Countries extends u.BukModelColl {get cls() {return Country}}

class ViewMeta extends u.Model {
  static spec = {
    ...super.spec,
    // createdAt: u.toValidDateOpt,
    personViews: a.reqFin,
    uniqueViews: a.reqFin,
    totalViews: a.reqFin,
  }

  pk() {return this.id}
}

class ViewMetas extends u.BukModelColl {get cls() {return ViewMeta}}

class Language extends a.Struct {
  static spec = {
    ...super.spec,
    code: a.reqValidStr,
    title: a.reqValidStr,
    isHidden: a.laxBool,
  }

  // Technically the primary key is `languages.id`, but in practice we actually
  // use `languages.code` as the foreign key.
  pk() {return this.code}
}

export class Languages extends u.ModelColl {get cls() {return Language}}

export class StripePaymentMethod extends u.BukModel {
  static spec = {
    ...super.spec,
    type: a.laxStr,
    title: a.laxStr,
    createdAt: val => a.toInstOpt(val, a.DateValid),
  }
}

class StripePaymentMethods extends u.ModelColl {
  get cls() {return StripePaymentMethod}
}

export class StripePaymentMethodPage extends u.BukPage {
  coll() {return this.buk().get.sets?.stripePaymentMethods}
}

export class StripePaymentMethodPages extends u.BukPages {
  get cls() {return StripePaymentMethodPage}
}

export class ServicePpcMeta extends a.Struct {
  static spec = {
    ...super.spec,
    priceViewEntPreviewUsdCents: a.reqInt,
    priceViewEntDetailsUsdCents: a.reqInt,
    estimatedDailyBudgetUsdCents: a.reqInt,
    estimatedWeeklyBudgetUsdCents: a.reqInt,
    estimatedMonthlyBudgetUsdCents: a.reqInt,
    actExpiration: a.reqValidStr,
    avgConversion: a.reqFinPos,
    weekPreviewViews: a.reqInt,
    weekDetailsViews: a.reqInt,
    monthPreviewViews: a.reqInt,
    monthDetailsViews: a.reqInt,
    dailyBudgetMinUsdCents: a.reqInt,
  }
}

// Semi-placeholder, TODO define fields.
export class Act extends u.BukModel {}

class Acts extends u.ModelColl {get cls() {return Act}}

export class ActPage extends u.BukPage {
  coll() {return this.buk().get.sets?.acts}
}

export class ActPages extends u.BukPages {get cls() {return ActPage}}

export class GiftCard extends u.Model {
  static spec = {
    ...super.spec,
    id: a.reqValidStr,
    status: a.optStr,
    currency: a.optStr,
    cents: a.optFin,
    consumerEmail: a.optStr,
    createdAt: val => a.toInstOpt(val, a.DateValid),
    activatedAt: val => a.toInstOpt(val, a.DateValid), // Synthetic field.
  }

  // Non-standard fields for GA event.
  gtagOpt() {return {entType: c.ENT_TYPE_GIFT_CARD}}

  // Standard fields for GA item.
  gtagItem() {
    return {
      item_id: this.id,
      item_name: this.slug || this.title,
      currency: u.currencyToGtag(this.currency),
      price: u.centsToGtag(this.cents),
    }
  }
}

export class Deposit extends u.Model {
  static spec = {
    ...super.spec,
    id: a.reqValidStr,
    currency: a.optStr,
    cents: a.optFin,
    entId: a.optStr,
  }

  // Non-standard fields for GA event.
  gtagOpt() {return {entType: c.VIRT_TYPE_DEPOSIT, entId: this.entId}}

  // Standard fields for GA item.
  gtagItem() {
    return {
      item_id: this.id,
      item_name: this.slug || this.title,
      currency: u.currencyToGtag(this.currency),
      price: u.centsToGtag(this.cents),
    }
  }
}

export class Receipt extends u.BukModel {
  static spec = {
    ...super.spec,
    id: a.reqValidStr,
    currency: a.optStr,
    cents: a.optFin,
    startAt: u.toValidDate,
    nextStartAt: u.toValidDate,
  }
}

class Receipts extends u.BukModelColl {get cls() {return Receipt}}

export class ReceiptPage extends u.BukPage {
  coll() {return this.buk().get.sets?.receipts}
}

export class ReceiptPages extends u.BukPages {get cls() {return ReceiptPage}}
